import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Target, TrendingUp, Award, BookOpen, Eye, ChevronDown, ChevronUp, CheckCircle, XCircle, RotateCcw, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../hooks/useUser';
import { useLearningMode } from '../contexts/LearningModeContext';
import { PaginatedResponse, ExerciseWithAttempts, ConceptCategory, LearningMode, getConceptGroupsForMode } from '@unity-learning/shared';
import { getCertificationAttempts, getExerciseHistory, getExerciseMiniCours, getKnowledgeNodes, getMiniCoursLibrary, type MiniCoursLibraryItem } from '../services/api';
import ReactMarkdown from 'react-markdown';
import CompletionWheel from '../components/CompletionWheel';
import MiniCoursContent from '../components/MiniCoursContent';
import { trackEvent } from '../utils/analytics';

interface UserStats {
  totalExercises: number;
  totalSuccesses: number;
  totalTimeSpent: number;
  currentStreak: number;
  successRate: number;
  conceptStats: {
    concept: ConceptCategory;
    attempts: number;
    successes: number;
  }[];
}

// Concepts présents dans les groupes d'un mode mais qu'aucun flux de génération ne propose
// aujourd'hui (aucune carte d'accueil, aucun nœud de parcours) : on n'affiche pas de barre
// morte à 0, mais s'ils ont été pratiqués par le passé leur barre s'affiche normalement.
const UNREACHABLE_CONCEPTS: Partial<Record<LearningMode, ConceptCategory[]>> = {
  [LearningMode.PYTHON_JS]: [ConceptCategory.NAMING_CONVENTIONS],
  [LearningMode.GODOT]: [ConceptCategory.NAMING_CONVENTIONS, ConceptCategory.COMMENTS]
};

interface CertificationAttempt {
  id: string;
  examId: string;
  startedAt: string;
  finishedAt: string;
  score: number;
  maxScore: number;
  passed: boolean;
}

const ProgressionPage: React.FC = () => {
  const { user } = useUser();
  const { learningMode } = useLearningMode();
  const navigate = useNavigate();
  const [stats, setStats] = useState<UserStats | null>(null);
  const [recentExercises, setRecentExercises] = useState<ExerciseWithAttempts[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedExercises, setExpandedExercises] = useState<Set<string>>(new Set());
  const [miniCoursData, setMiniCoursData] = useState<Record<string, any[]>>({});
  const [certificationAttempts, setCertificationAttempts] = useState<CertificationAttempt[]>([]);
  const [isCertHistoryExpanded, setIsCertHistoryExpanded] = useState(false);
  const [miniCoursLibrary, setMiniCoursLibrary] = useState<MiniCoursLibraryItem[]>([]);
  const [miniCoursLibraryTotal, setMiniCoursLibraryTotal] = useState(0);
  const [isLibraryExpanded, setIsLibraryExpanded] = useState(false);
  const [selectedMiniCours, setSelectedMiniCours] = useState<MiniCoursLibraryItem | null>(null);
  const [kpTotals, setKpTotals] = useState<{ total: number; completed: number } | null>(null);
  const LIBRARY_PAGE_SIZE = 50;

  useEffect(() => {
    const fetchProgressionData = async () => {
      if (!user) return;

      console.log('📊 Récupération des données de progression pour:', user.pseudonym);
      setIsLoading(true);
      setError(null);

      try {
        // Récupérer l'historique des exercices (tous les exercices)
        let history: PaginatedResponse<ExerciseWithAttempts> | null = null;
        try {
          history = await getExerciseHistory(user.id, { learningMode });
          console.log('📦 Données d\'historique reçues:', history);
        } catch (historyError) {
          console.warn('⚠ Historique indisponible:', (historyError as Error).message);
        }

        if (history) {
          {
            const exercises = history.items;
            setRecentExercises(exercises);

            // Calculer les statistiques
            const totalExercises = exercises.length;
            const totalSuccesses = exercises.filter(ex => ex.userAttempt?.isCorrect).length;
            const totalTimeSpent = exercises.reduce((sum, ex) => sum + (ex.userAttempt?.timeSpent || 0), 0);
            const successRate = totalExercises > 0 ? Math.round((totalSuccesses / totalExercises) * 100) : 0;

            // Calculer les stats par concept
            const conceptMap = new Map<ConceptCategory, { attempts: number; successes: number }>();
            exercises.forEach(ex => {
              ex.concepts.forEach(concept => {
                const current = conceptMap.get(concept) || { attempts: 0, successes: 0 };
                current.attempts++;
                if (ex.userAttempt?.isCorrect) current.successes++;
                conceptMap.set(concept, current);
              });
            });

            const conceptStats = Array.from(conceptMap.entries()).map(([concept, data]) => ({
              concept,
              attempts: data.attempts,
              successes: data.successes
            }));

            setStats({
              totalExercises,
              totalSuccesses,
              totalTimeSpent: Math.round(totalTimeSpent / 60), // Convert to minutes
              currentStreak: 0, // TODO: Calculate actual streak
              successRate,
              conceptStats
            });
          }
        } else {
          console.warn('⚠ Pas d\'historique trouvé, stats par défaut');
          setStats({
            totalExercises: 0,
            totalSuccesses: 0,
            totalTimeSpent: 0,
            currentStreak: 0,
            successRate: 0,
            conceptStats: []
          });
        }

        // Fetch certification attempts
        try {
          setCertificationAttempts(await getCertificationAttempts<CertificationAttempt>());
        } catch (certErr) {
          console.warn('Could not load certification data');
        }

        // Bibliothèque des mini-cours du mode courant (best-effort, ne bloque pas la page)
        try {
          const library = await getMiniCoursLibrary({ learningMode, limit: LIBRARY_PAGE_SIZE });
          if (library) {
            setMiniCoursLibrary(library.items);
            setMiniCoursLibraryTotal(library.total);
          }
        } catch (libraryErr) {
          console.warn('Could not load mini-cours library');
        }

        // B3 — Roue de complétion : total de points de connaissance validés du mode
        // (somme des compteurs par nœud servis par /api/knowledge/nodes, best-effort)
        setKpTotals(null);
        try {
          const nodes = await getKnowledgeNodes(user.id, learningMode);
          if (Array.isArray(nodes)) {
            setKpTotals({
              total: nodes.reduce((sum, n) => sum + (n.kp_count || 0), 0),
              completed: nodes.reduce((sum, n) => sum + (n.completed_count || 0), 0)
            });
          }
        } catch (kpErr) {
          console.warn('Could not load knowledge point totals');
        }
      } catch (err) {
        console.error('❌ Erreur lors du chargement des stats:', err);
        setError((err as Error).message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProgressionData();
  }, [user, learningMode]);

  // Charger la page suivante de la bibliothèque de mini-cours
  const loadMoreMiniCours = async () => {
    try {
      const page = await getMiniCoursLibrary({ learningMode, limit: LIBRARY_PAGE_SIZE, offset: miniCoursLibrary.length });
      if (page) {
        setMiniCoursLibrary(prev => [...prev, ...page.items]);
        setMiniCoursLibraryTotal(page.total);
      }
    } catch (error) {
      console.error('Error loading more mini-courses:', error);
    }
  };

  const openMiniCours = (item: MiniCoursLibraryItem) => {
    trackEvent('mini_course_opened', { exerciseId: item.exerciseId, source: 'library' });
    setSelectedMiniCours(item);
  };

  // Function to toggle exercise expansion and load mini-courses
  const toggleExerciseExpansion = async (exerciseId: string) => {
    const newExpanded = new Set(expandedExercises);

    if (expandedExercises.has(exerciseId)) {
      newExpanded.delete(exerciseId);
    } else {
      newExpanded.add(exerciseId);

      // Load mini-courses for this exercise if not already loaded
      if (!miniCoursData[exerciseId] && user) {
        try {
          const records = await getExerciseMiniCours(exerciseId, user.id);
          setMiniCoursData(prev => ({
            ...prev,
            [exerciseId]: [...(records ?? [])].sort((a, b) => a.simplification_level - b.simplification_level)
          }));
        } catch (error) {
          console.error('Error loading mini-courses:', error);
        }
      }
    }

    setExpandedExercises(newExpanded);
  };

  const getConceptDisplayName = (concept: ConceptCategory): string => {
    // Libellés ajustés pour les concepts partagés entre modes (pas de jargon C# hors Unity)
    if (learningMode !== LearningMode.UNITY_CSHARP && concept === ConceptCategory.METHODS) {
      return 'Fonctions';
    }
    const names: Record<ConceptCategory, string> = {
      [ConceptCategory.DATA_TYPES]: 'Types de données',
      [ConceptCategory.VARIABLES]: 'Variables',
      [ConceptCategory.ACCESS_MODIFIERS]: 'Modificateurs d\'accès',
      [ConceptCategory.METHODS]: 'Méthodes',
      [ConceptCategory.OPERATORS]: 'Opérateurs',
      [ConceptCategory.CONDITIONALS]: 'Conditions',
      [ConceptCategory.LOOPS]: 'Boucles',
      [ConceptCategory.ENUMS]: 'Énumérations',
      [ConceptCategory.COMMENTS]: 'Commentaires',
      [ConceptCategory.ERROR_IDENTIFICATION]: 'Identification d\'erreurs',
      [ConceptCategory.NAMING_CONVENTIONS]: 'Conventions de nommage',
      [ConceptCategory.ABSTRACT_CLASSES]: 'Classes abstraites',
      [ConceptCategory.INTERFACES]: 'Interfaces',
      [ConceptCategory.MONOBEHAVIOUR_LIFECYCLE]: 'Cycle de vie MonoBehaviour',
      [ConceptCategory.INPUT_HANDLING]: 'Gestion des entrées',
      [ConceptCategory.GAMEOBJECT_MANIPULATION]: 'Manipulation GameObjects',
      [ConceptCategory.COMPONENT_ACCESS]: 'Accès aux composants',
      [ConceptCategory.PHYSICS_RIGIDBODY]: 'Physique 2D (Rigidbody2D)',
      [ConceptCategory.VECTOR_MATH]: 'Mathématiques vectorielles',
      [ConceptCategory.COROUTINES]: 'Coroutines',
      [ConceptCategory.ANIMATION_SYSTEM]: 'Système d\'animation',
      [ConceptCategory.UI_INTERACTION]: 'Interaction UI',
      [ConceptCategory.DEBUGGING]: 'Débogage',
      [ConceptCategory.PYJS_PRINT_OUTPUT]: 'Affichage (print / console.log)',
      [ConceptCategory.PYJS_USER_INPUT]: 'Saisie utilisateur',
      [ConceptCategory.PYJS_STRINGS]: 'Manipulation de chaînes',
      [ConceptCategory.PYJS_LISTS_ARRAYS]: 'Listes et tableaux',
      [ConceptCategory.PYJS_DICTIONARIES]: 'Dictionnaires / Objets',
      [ConceptCategory.PYJS_TYPE_CONVERSION]: 'Conversion de types',
      [ConceptCategory.GODOT_PRINT_OUTPUT]: 'Affichage console Godot (print)',
      [ConceptCategory.GODOT_ARRAYS_DICTS]: 'Array & Dictionary',
      [ConceptCategory.GODOT_NODES_SCENES]: 'Nodes et Scènes',
      [ConceptCategory.GODOT_LIFECYCLE]: 'Cycle de vie Godot',
      [ConceptCategory.GODOT_SIGNALS]: 'Signaux',
      [ConceptCategory.GODOT_AWAIT_CALLABLES]: 'Await & Callbacks',
      [ConceptCategory.GODOT_TYPED_GDSCRIPT]: 'GDScript avancé',
      [ConceptCategory.GODOT_VECTOR_MATH]: 'Vecteurs (Vector2)',
      [ConceptCategory.GODOT_SCENE_INSTANCING]: 'Instanciation de scènes',
      [ConceptCategory.GODOT_CHARACTER_MOVEMENT]: 'Mouvement du personnage',
      [ConceptCategory.GODOT_OOP_PATTERNS]: 'POO & Patterns'
    };
    return names[concept] || concept;
  };

  // Toutes les barres du mode courant : les groupes de concepts partagés (source de vérité
  // commune avec la génération), fusionnés avec les stats réellement pratiquées.
  const practicedByConcept = new Map<ConceptCategory, { concept: ConceptCategory; attempts: number; successes: number }>(
    (stats?.conceptStats || []).map(s => [s.concept, s] as const)
  );
  const conceptGroups = getConceptGroupsForMode(learningMode) as Record<string, readonly ConceptCategory[]>;
  const unreachableConcepts = new Set(UNREACHABLE_CONCEPTS[learningMode] || []);
  const groupedConcepts = Object.entries(conceptGroups)
    .map(([title, concepts]) => ({
      title,
      concepts: concepts.filter(c => practicedByConcept.has(c) || !unreachableConcepts.has(c))
    }))
    .filter(group => group.concepts.length > 0);
  const conceptsInGroups = new Set<ConceptCategory>(Object.values(conceptGroups).flat());
  // Concepts pratiqués mais absents des groupes du mode (anciennes données, exercices
  // inter-modes) : affichés quand même pour ne rien perdre.
  const orphanConcepts = (stats?.conceptStats || [])
    .filter(s => !conceptsInGroups.has(s.concept))
    .map(s => s.concept);

  const renderConceptBar = (concept: ConceptCategory) => {
    const stat = practicedByConcept.get(concept);
    if (!stat) {
      return (
        <div key={concept} className="flex items-center justify-between p-3 bg-gray-50/50 dark:bg-gray-700/30 rounded-lg border border-gray-100 dark:border-gray-700/50 opacity-60">
          <div className="flex-1">
            <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
              {getConceptDisplayName(concept)}
            </div>
            <div className="text-xs text-gray-400 dark:text-gray-500">
              Pas encore pratiqué
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-24 bg-gray-200 dark:bg-gray-600 rounded-full h-2"></div>
            <span className="text-sm font-medium text-gray-400 dark:text-gray-500 w-8 text-right">
              —
            </span>
          </div>
        </div>
      );
    }
    const rate = stat.attempts > 0 ? Math.round((stat.successes / stat.attempts) * 100) : 0;
    return (
      <div key={concept} className="flex items-center justify-between p-3 bg-gray-50/50 dark:bg-gray-700/30 rounded-lg border border-gray-100 dark:border-gray-700/50">
        <div className="flex-1">
          <div className="text-sm font-medium text-gray-900 dark:text-white">
            {getConceptDisplayName(concept)}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {stat.successes}/{stat.attempts} exercices réussis
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <div className="w-24 bg-gray-200 dark:bg-gray-600 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full"
              style={{ width: `${rate}%` }}
            ></div>
          </div>
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 w-8 text-right">
            {rate}%
          </span>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Chargement des statistiques...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg">
        <strong className="font-bold">Erreur : </strong>
        <span className="block sm:inline">{error}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="glass dark:glass-dark rounded-xl p-8 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl -mr-32 -mt-32 pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-green-500/10 rounded-full blur-3xl -ml-32 -mb-32 pointer-events-none"></div>
        <div className="relative z-10 flex items-center justify-between gap-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2 flex items-center">
              <TrendingUp className="w-8 h-8 mr-3 text-blue-600 dark:text-blue-400" />
              Votre Progression
            </h1>
            <p className="text-gray-600 dark:text-slate-300 text-lg">
              Suivez vos progrès et découvrez vos points forts en programmation{' '}
              {learningMode === LearningMode.GODOT ? 'GDScript' : learningMode === LearningMode.PYTHON_JS ? 'Python / JavaScript' : 'Unity C#'}.
            </p>
          </div>
          {/* B3 — Roue de complétion du parcours du mode courant */}
          {kpTotals && kpTotals.total > 0 && (
            <div className="hidden sm:flex flex-col items-center shrink-0">
              <CompletionWheel completed={kpTotals.completed} total={kpTotals.total} size={110} strokeWidth={12} />
              <span className="text-xs text-gray-600 dark:text-slate-300 mt-2 text-center">
                {kpTotals.completed} / {kpTotals.total} points de<br />connaissance validés
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Statistiques principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="glass dark:glass-dark rounded-xl p-6 hover:scale-105 transition-transform duration-300">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg flex items-center justify-center mr-4">
              <BookOpen className="w-6 h-6" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats?.totalExercises || 0}</div>
              <div className="text-sm text-gray-600 dark:text-slate-300">Exercices tentés</div>
            </div>
          </div>
        </div>

        <div className="glass dark:glass-dark rounded-xl p-6 hover:scale-105 transition-transform duration-300">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-lg flex items-center justify-center mr-4">
              <Award className="w-6 h-6" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats?.totalSuccesses || 0}</div>
              <div className="text-sm text-gray-600 dark:text-slate-300">Exercices réussis</div>
            </div>
          </div>
        </div>

        <div className="glass dark:glass-dark rounded-xl p-6 hover:scale-105 transition-transform duration-300">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded-lg flex items-center justify-center mr-4">
              <Target className="w-6 h-6" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats?.successRate || 0}%</div>
              <div className="text-sm text-gray-600 dark:text-slate-300">Taux de réussite</div>
            </div>
          </div>
        </div>

        <div className="glass dark:glass-dark rounded-xl p-6 hover:scale-105 transition-transform duration-300">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 rounded-lg flex items-center justify-center mr-4">
              <Clock className="w-6 h-6" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{stats?.totalTimeSpent || 0}</div>
              <div className="text-sm text-gray-600 dark:text-slate-300">Minutes d'apprentissage</div>
            </div>
          </div>
        </div>
      </div>

      {/* Certification History */}
      {certificationAttempts.length > 0 && (
        <div className="glass dark:glass-dark rounded-xl p-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <Award className="w-5 h-5 mr-2 text-indigo-600 dark:text-indigo-400" />
            Historique des Certifications
          </h2>
          <div className="space-y-3">
            {certificationAttempts.slice(0, isCertHistoryExpanded ? undefined : 3).map((attempt) => {
              const percentage = Math.round((attempt.score / attempt.maxScore) * 100);
              return (
                <div
                  key={attempt.id}
                  onClick={() => navigate(`/certification/results/${attempt.id}`)}
                  className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-indigo-500 dark:hover:border-indigo-400 cursor-pointer transition-all duration-200 hover:shadow-md"
                >
                  <div className="flex items-center space-x-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${attempt.passed
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                      : 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                      }`}>
                      {attempt.passed ? (
                        <CheckCircle className="w-6 h-6" />
                      ) : (
                        <XCircle className="w-6 h-6" />
                      )}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        Unity Certified User: Programmer
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {new Date(attempt.finishedAt).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <div className={`text-lg font-bold ${percentage >= 70 ? 'text-green-600 dark:text-green-400' :
                        percentage >= 50 ? 'text-yellow-600 dark:text-yellow-400' :
                          'text-red-600 dark:text-red-400'
                        }`}>
                        {percentage}%
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {attempt.score}/{attempt.maxScore} pts
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${attempt.passed
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                      : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                      }`}>
                      {attempt.passed ? 'Réussi' : 'Échoué'}
                    </span>
                    <Eye className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                  </div>
                </div>
              );
            })}
          </div>


          {certificationAttempts.length > 3 && (
            <div className="mt-3 text-center">
              <button
                onClick={() => setIsCertHistoryExpanded(!isCertHistoryExpanded)}
                className="text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 flex items-center justify-center mx-auto transition-colors"
              >
                {isCertHistoryExpanded ? (
                  <>
                    <ChevronUp className="w-4 h-4 mr-1" />
                    Voir moins
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4 mr-1" />
                    Voir plus ({certificationAttempts.length - 3} restants)
                  </>
                )}
              </button>
            </div>
          )}

          <div className="mt-4 text-center">
            <button
              onClick={() => navigate('/certification')}
              className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-medium"
            >
              Passer une certification →
            </button>
          </div>
        </div>
      )
      }

      {/* Progression par concept : toutes les barres du mode, y compris non pratiquées */}
      {
        stats && (
          <div className="glass dark:glass-dark rounded-xl p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Progression par concept</h2>
            <div className="space-y-6">
              {groupedConcepts.map(group => (
                <div key={group.title}>
                  <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
                    {group.title}
                  </h3>
                  <div className="space-y-3">
                    {group.concepts.map(renderConceptBar)}
                  </div>
                </div>
              ))}
              {orphanConcepts.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
                    Autres concepts pratiqués
                  </h3>
                  <div className="space-y-3">
                    {orphanConcepts.map(renderConceptBar)}
                  </div>
                </div>
              )}
            </div>
          </div>
        )
      }

      {/* Mes mini-cours : bibliothèque de tous les mini-cours générés (B2) */}
      <div className="glass dark:glass-dark rounded-xl p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <BookOpen className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" />
          Mes mini-cours
        </h2>

        {miniCoursLibrary.length > 0 ? (
          <>
            <div className="space-y-3">
              {miniCoursLibrary.slice(0, isLibraryExpanded ? undefined : 5).map((item) => (
                <button
                  key={item.id}
                  onClick={() => openMiniCours(item)}
                  className="w-full flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-400 cursor-pointer transition-all duration-200 hover:shadow-md text-left"
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {item.exerciseTitle ? (
                        <ReactMarkdown
                          components={{
                            strong: ({ children }) => <strong className="font-bold">{children}</strong>,
                            code: ({ children }) => <code className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-1 py-0.5 rounded text-xs font-mono">{children}</code>,
                            p: ({ children }) => <span>{children}</span>
                          }}
                        >
                          {item.exerciseTitle}
                        </ReactMarkdown>
                      ) : (
                        <span className="italic">Exercice d'origine introuvable</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                      Généré le {new Date(item.generatedAt).toLocaleDateString('fr-FR')}
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 ml-3 shrink-0">
                    <span className="text-xs font-medium text-blue-800 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30 px-2 py-1 rounded">
                      Niveau {item.simplificationLevel === 0 ? 'Standard' : item.simplificationLevel}
                    </span>
                    {item.language !== 'fr' && (
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded uppercase">
                        {item.language}
                      </span>
                    )}
                    <Eye className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                  </div>
                </button>
              ))}
            </div>

            {(miniCoursLibraryTotal > 5 || miniCoursLibrary.length > 5) && (
              <div className="mt-3 text-center">
                {!isLibraryExpanded ? (
                  <button
                    onClick={() => setIsLibraryExpanded(true)}
                    className="text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 flex items-center justify-center mx-auto transition-colors"
                  >
                    <ChevronDown className="w-4 h-4 mr-1" />
                    Voir plus ({miniCoursLibraryTotal - 5} restants)
                  </button>
                ) : miniCoursLibrary.length < miniCoursLibraryTotal ? (
                  <button
                    onClick={loadMoreMiniCours}
                    className="text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 flex items-center justify-center mx-auto transition-colors"
                  >
                    <ChevronDown className="w-4 h-4 mr-1" />
                    Charger plus ({miniCoursLibraryTotal - miniCoursLibrary.length} restants)
                  </button>
                ) : (
                  <button
                    onClick={() => setIsLibraryExpanded(false)}
                    className="text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 flex items-center justify-center mx-auto transition-colors"
                  >
                    <ChevronUp className="w-4 h-4 mr-1" />
                    Voir moins
                  </button>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="text-sm text-gray-600 dark:text-gray-400 italic text-center py-4">
            Aucun mini-cours pour l'instant — ils apparaîtront ici quand vous en générerez depuis un exercice.
          </div>
        )}
      </div>

      {/* Historique des exercices */}
      {
        recentExercises.length > 0 && (
          <div className="glass dark:glass-dark rounded-xl p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <Calendar className="w-5 h-5 mr-2" />
              Historique des exercices
            </h2>
            <div className="space-y-3">
              {recentExercises.map((exercise) => (
                <div key={exercise.id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <div className="flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 group">
                    <button
                      onClick={() => navigate(`/exercise/${exercise.id}/review/${exercise.userAttempt?.id}`)}
                      className="flex-1 flex items-center justify-between text-left"
                    >
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400">
                          <ReactMarkdown
                            components={{
                              strong: ({ children }) => <strong className="font-bold">{children}</strong>,
                              code: ({ children }) => <code className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-1 py-0.5 rounded text-xs font-mono">{children}</code>,
                              p: ({ children }) => <span>{children}</span>
                            }}
                          >
                            {exercise.title}
                          </ReactMarkdown>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          <ReactMarkdown
                            components={{
                              strong: ({ children }) => <strong className="font-bold text-blue-700 dark:text-blue-300">{children}</strong>,
                              code: ({ children }) => <code className="bg-gray-100 text-gray-700 px-1 py-0.5 rounded text-xs font-mono">{children}</code>,
                              p: ({ children }) => <span>{children}</span>
                            }}
                          >
                            {exercise.description}
                          </ReactMarkdown>
                        </div>
                        <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                          Soumis le {new Date(exercise.userAttempt?.submittedAt || '').toLocaleDateString('fr-FR')}
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className={`px-2 py-1 rounded-full text-xs ${exercise.userAttempt?.isCorrect
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                          : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                          }`}>
                          {exercise.userAttempt?.isCorrect ? 'Réussi' : 'Échoué'}
                        </span>
                        {exercise.totalAttempts > 0 && (
                          <span
                            className="flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
                            title={`${exercise.totalAttempts} soumission${exercise.totalAttempts > 1 ? 's' : ''} sur cet exercice`}
                          >
                            <RotateCcw className="w-3 h-3 mr-1" />
                            {exercise.totalAttempts} {exercise.totalAttempts > 1 ? 'tentatives' : 'tentative'}
                          </span>
                        )}
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {exercise.userAttempt?.timeSpent ?
                            (exercise.userAttempt.timeSpent < 60 ?
                              `${exercise.userAttempt.timeSpent}s` :
                              `${Math.round(exercise.userAttempt.timeSpent / 60)}m`
                            ) : '0s'
                          }
                        </span>
                        <Eye className="w-4 h-4 text-gray-400 dark:text-gray-500 group-hover:text-blue-600 dark:group-hover:text-blue-400" />
                      </div>
                    </button>

                    <button
                      onClick={() => toggleExerciseExpansion(exercise.id)}
                      className="ml-3 p-2 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                      title="Voir les mini-cours générés"
                    >
                      <BookOpen className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      {expandedExercises.has(exercise.id) ? (
                        <ChevronUp className="w-3 h-3 text-gray-400 mt-1" />
                      ) : (
                        <ChevronDown className="w-3 h-3 text-gray-400 mt-1" />
                      )}
                    </button>
                  </div>

                  {/* Mini-courses section */}
                  {expandedExercises.has(exercise.id) && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 border-t border-blue-200 dark:border-blue-800 p-4">
                      <h4 className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-3 flex items-center">
                        <BookOpen className="w-4 h-4 mr-2" />
                        Mini-cours générés
                      </h4>

                      {miniCoursData[exercise.id] && miniCoursData[exercise.id].length > 0 ? (
                        <div className="space-y-3">
                          {miniCoursData[exercise.id].map((miniCours, index) => (
                            <div key={miniCours.id} className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-blue-200 dark:border-blue-800">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-medium text-blue-800 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30 px-2 py-1 rounded">
                                  Niveau {miniCours.simplification_level === 0 ? 'Standard' : miniCours.simplification_level}
                                </span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {new Date(miniCours.generated_at).toLocaleDateString('fr-FR')}
                                </span>
                              </div>
                              <div className="prose prose-sm max-w-none">
                                <ReactMarkdown
                                  components={{
                                    strong: ({ children }) => (
                                      <strong className="font-bold text-blue-800 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30 px-1 rounded text-xs">
                                        {children}
                                      </strong>
                                    ),
                                    blockquote: ({ children }) => (
                                      <blockquote className="border-l-2 border-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 pl-2 py-1 my-2 italic text-yellow-800 dark:text-yellow-200 text-xs">
                                        {children}
                                      </blockquote>
                                    ),
                                    code: ({ children }) => (
                                      <code className="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-1 py-0.5 rounded text-xs font-mono">
                                        {children}
                                      </code>
                                    ),
                                    ul: ({ children }) => <ul className="list-disc pl-4 mb-2 text-gray-700 dark:text-gray-300 text-xs">{children}</ul>,
                                    ol: ({ children }) => <ol className="list-decimal pl-4 mb-2 text-gray-700 dark:text-gray-300 text-xs">{children}</ol>,
                                    li: ({ children }) => <li className="mb-1">{children}</li>,
                                    p: ({ children }) => <p className="mb-2 text-gray-700 dark:text-gray-300 text-xs leading-relaxed">{children}</p>,
                                  }}
                                >
                                  {miniCours.explanation}
                                </ReactMarkdown>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-sm text-gray-600 dark:text-gray-400 italic text-center py-4">
                          Aucun mini-cours généré pour cet exercice
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )
      }

      {
        (!stats || stats.totalExercises === 0) && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-200 px-4 py-3 rounded-lg text-center">
            <div className="font-medium">Aucun exercice terminé</div>
            <div className="text-sm">Commencez par faire quelques exercices pour voir vos statistiques ici !</div>
          </div>
        )
      }

      {/* Modale de lecture d'un mini-cours de la bibliothèque */}
      {selectedMiniCours && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4"
          onClick={() => setSelectedMiniCours(null)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-start space-x-3 min-w-0">
                <BookOpen className="w-6 h-6 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {selectedMiniCours.exerciseTitle ? (
                      <ReactMarkdown
                        components={{
                          strong: ({ children }) => <strong className="font-bold">{children}</strong>,
                          code: ({ children }) => <code className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-1 py-0.5 rounded text-sm font-mono">{children}</code>,
                          p: ({ children }) => <span>{children}</span>
                        }}
                      >
                        {selectedMiniCours.exerciseTitle}
                      </ReactMarkdown>
                    ) : (
                      <span className="italic">Exercice d'origine introuvable</span>
                    )}
                  </h2>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Niveau {selectedMiniCours.simplificationLevel === 0 ? 'Standard' : selectedMiniCours.simplificationLevel}
                    {' · '}Généré le {new Date(selectedMiniCours.generatedAt).toLocaleDateString('fr-FR')}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setSelectedMiniCours(null)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors shrink-0 ml-3"
                title="Fermer"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto">
              <MiniCoursContent markdown={selectedMiniCours.explanation} />
            </div>

            {selectedMiniCours.exerciseTitle && (
              <div className="p-4 border-t border-gray-200 dark:border-gray-700 text-right">
                <button
                  onClick={() => navigate(`/exercise/${selectedMiniCours.exerciseId}`)}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
                >
                  Revoir l'exercice d'origine →
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div >
  );
};

export default ProgressionPage;